'use client';
import styled, { css } from 'styled-components';
import { media } from '../../../theme/mixins/responsive';
import { ArrowContainer } from './arrow/styles';
import getButtonTheme from './get-button-theme';
const createButtonCSS = (name, theme) => css `
  font-size: ${theme.fonts[`button_${name}`]};
  line-height: normal;
  letter-spacing: ${theme.letterSpacing[`button_${name}`]}px;
  min-height: ${theme.buttons[`height_${name}`]};
  min-width: ${theme.buttons[`width_${name}`]};
  width: fit-content;
  max-width: 100%;
  ${name === 'large' ? `font-weight:600;` : ''}
  @media (max-width: ${theme.breakpoints.tablet}px) {
    font-size: ${theme.fonts[`button_${name}_md`]};
    min-height: ${theme.buttons[`height_${name}_md`]};
    min-width: ${theme.buttons[`width_${name}_md`]};
    letter-spacing: ${({ theme }) => theme.letterSpacing[`button_${name}_md`]}px;
  }
`;
const buttonLarge = (theme) => createButtonCSS('large', theme);
const buttonMedium = (theme) => createButtonCSS('medium', theme);
const buttonTertiary = (theme) => createButtonCSS('small', theme);
const buttonExtraSmall = (theme) => createButtonCSS('extra_small', theme);
const buttonAuto = css `
  min-width: auto;
  min-height: auto;
  gap: 0;
  span {
    margin: 0;
  }
`;
const buttonCircle = css `
  aspect-ratio: 1/1;
  padding: 8px;
  gap: 0;
  span {
    margin: 0;
  }
`;
const buttonSquare = css `
  aspect-ratio: 1/1;
  padding: 8px;
  gap: 0;
  border-radius: 2px;
  span {
    margin: 0;
  }
`;
const getButtonSizeStyling = ({ size, theme }) => {
    let rules;
    switch (size) {
        case 'extra-small':
            rules = buttonExtraSmall(theme);
            break;
        case 'small':
            rules = buttonTertiary(theme);
            break;
        case 'large':
            rules = buttonLarge(theme);
            break;
        case 'auto':
            rules = buttonAuto;
            break;
        case 'circle':
            rules = buttonCircle;
            break;
        case 'square':
            rules = buttonSquare;
            break;
        default:
            rules = buttonMedium(theme);
            break;
    }
    return css `
    width: fit-content;
    .ant-btn-icon,
    .ant-btn-icon + span {
      margin-left: 0;
    }

    ${rules}
  `;
};
export const getButtonColorStyling = (props) => {
    const { theme, defaultTheme } = props;
    const buttonTheme = getButtonTheme(theme, defaultTheme);
    return css `
    color: ${buttonTheme.textColor};
    background: ${buttonTheme.backgroundColor};
    border: 1px solid ${buttonTheme.borderColor};

    svg,
    ${ArrowContainer} {
      color: ${buttonTheme.textColor};
      &::after {
        border-color: ${buttonTheme.textColor};
      }
    }

    &:hover,
    &:active {
      &:not(:disabled) {
        ${getButtonColorStylingHover(Object.assign(Object.assign({}, props), { buttonTheme }))}
      }
    }
  `;
};
const getButtonColorStylingHover = ({ buttonTheme, }) => {
    return css `
    color: ${buttonTheme.textColorHover};
    background: ${buttonTheme.backgroundColorHover};
    border: 1px solid ${buttonTheme.borderColorHover};

    svg,
    ${ArrowContainer} {
      color: ${buttonTheme.textColorHover};
      &::after {
        border-color: ${buttonTheme.textColorHover};
      }
    }
  `;
};
const getLinkTheme = (theme, color) => {
    var _a;
    const linkThemes = {
        [theme.colors.grey_1]: {
            textColor: theme.colors.grey_1,
        },
        [theme.colors.grey_3]: {
            textColor: theme.colors.grey_3,
        },
        [theme.colors.grey_4]: {
            textColor: theme.colors.grey_4,
        },
        [theme.colors.grey_5]: {
            textColor: theme.colors.grey_5,
        },
        [theme.colors.purple_1]: {
            textColor: theme.colors.purple_1,
        },
        [theme.colors.purple_2]: {
            textColor: theme.colors.purple_2,
        },
        [theme.colors.white_1]: {
            textColor: theme.colors.white_1,
        },
    };
    return (_a = linkThemes[color]) !== null && _a !== void 0 ? _a : { textColor: theme.colors.grey_1 };
};
const getTransparentHoverStyling = (theme) => css `
  transition: ${theme.appearance.transition};
  filter: brightness(3);
`;
export const getTransparentStyling = (props) => {
    const { theme, defaultTheme } = props;
    const linkTheme = getLinkTheme(theme, defaultTheme);
    return css `
    ${getButtonSizeStyling(props)}
    font-weight:400;
    background-color: transparent;
    border-radius: 0;
    padding: 0;
    outline: none;
    width: fit-content;
    transition: ${theme.appearance.transition};
    border: none;
    color: ${linkTheme.textColor};

    min-width: auto;

    svg,
    ${ArrowContainer} {
      color: ${linkTheme.textColor};
      &:after {
        border-color: ${linkTheme.textColor};
      }
    }

    &:focus {
      outline: none;
      border: none;
    }
    &:before,
    &:after {
      display: none;
    }
    &:hover,
    &:active {
      &:not(:disabled) {
        ${getTransparentHoverStyling(theme)}
      }
    }
  `;
};
export const getLinkStyling = (props) => css `
  ${getTransparentStyling(props)}
  span {
    margin: 0;
  }
  text-decoration: underline;
  text-underline-offset: 6px;
  min-width: auto;
  gap: 8px;
`;
export const getButtonStyling = (props) => {
    const { theme, defaultTheme } = props;
    const buttonTheme = getButtonTheme(theme, defaultTheme);
    return css `
    font-family: 'Inter';
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    line-height: normal;
    border-radius: ${theme.appearance.border_radius_button};
    background-color: ${buttonTheme.backgroundColor};
    border: 1px solid ${buttonTheme.borderColor};
    color: ${buttonTheme.textColor};

    svg,
    ${ArrowContainer} {
      color: ${buttonTheme.textColor};
      &:after {
        border-color: ${buttonTheme.textColor};
      }
    }

    box-shadow: none;
    white-space: nowrap;
    transition: ${theme.appearance.transition};
    cursor: pointer;
    gap: 8px;
    &:before,
    &:after {
      display: block;
      content: '';
    }
    a {
      color: inherit;
    }
    svg {
      color: ${theme.colors.white_1};
      display: block;
    }

    ${getButtonSizeStyling(props)};

    &:hover,
    &:active {
      &:not(:disabled) {
        ${ArrowContainer} {
          width: 30px;
        }
      }
    }

    &:disabled {
      filter: grayscale(1);
      opacity: 0.6;
    }
  `;
};
export const ButtonContainer = styled.button `
  ${({ $defaultTheme, $size, theme, $withArrow }) => getButtonStyling({
    theme,
    size: $size !== null && $size !== void 0 ? $size : 'small',
    defaultTheme: $defaultTheme !== null && $defaultTheme !== void 0 ? $defaultTheme : '',
    withArrow: !!$withArrow,
})}

  ${({ $xl, $default, $tablet }) => css `
    ${$default};
    ${media.xl `${$xl}`};
    ${media.xl `${$tablet}`};
  `}
`;
